import { useEffect, useRef, useState, useCallback } from "react";
import { X, Send, Loader2, Sparkles, Volume2, VolumeX, Square, MousePointerClick, Compass } from "lucide-react";
import { startTour, TOUR_KEY, sectionTourSteps } from "../lib/baraaTour";
import { toast } from "sonner";
import { splitPoint, collectUi, pointTo, timeGreeting, playVoice, stopVoice } from "../lib/baraa";
import { api } from "../lib/api";
import { BaraaBot } from "./BaraaBot";
import { useNavigate } from "react-router-dom";
import { matchCommand, splitOpen, openTarget, navCatalog, NAV_TARGETS } from "../lib/baraaNav";


const SECTION_LABELS = {
  overview: "الرئيسية", branches: "الفروع", analytics: "التحليلات",
  custody: "العُهد", vehicles: "المركبات والعُهد", company: "بيانات الشركة",
  correspondence: "المراسلات", riders: "المناديب - الأداء العام",
  "riders-daily": "المناديب - الأداء اليومي", "riders-compare": "مقارنة التطبيقات",
  "riders-target": "مستهدف المناديب", "riders-distances": "مسافات المناديب",
  wallets: "المحافظ", violations: "المخالفات", revenues: "الإيرادات",
  "reports-hub": "مركز التقارير", spareparts: "قطع الغيار",
  "car-maintenance": "صيانة السيارات", "moto-maintenance": "صيانة الدراجات",
  fuel: "الوقود", salaries: "الرواتب", leave: "الإجازات", injuries: "الإصابات",
  loyalty: "الولاء", purchases: "المشتريات", "perf-alerts": "تنبيهات الأداء",
  sales: "المبيعات", attendance: "الحضور", advances: "السلف", tasks: "المهام",
  alerts: "التنبيهات", employees: "الموظفون", lockers: "الخزائن",
};

const SUGGESTIONS = {
  _default: [
    "افتح كروت التشغيل",
    "كيف أبدأ باستخدام النظام؟",
    "كيف أضيف موظفاً جديداً؟",
    "كيف أسلّم مركبة لموظف؟",
    "كيف أستعيد كلمة مرور المالك؟",
  ],
  vehicles: [
    "كيف أضيف مركبة جديدة؟",
    "كيف أسلّم عهدة لموظف مع نموذج الفحص؟",
    "ما هي بطاقة التشغيل المشتركة وكيف أحدّثها؟",
  ],
  custody: ["كيف أسلّم عهدة لموظف؟", "كيف أُرجع عهدة من موظف؟", "أين أرى سجل العُهد؟"],
  riders: ["كيف أرفع ملف الطلبات؟", "كيف أقارن بين التطبيقات؟", "كيف أصدّر تقرير المناديب؟"],
  "riders-daily": ["كيف أرفع تقرير الطلبات اليومي؟", "كيف أقرأ الأداء اليومي؟"],
  "riders-compare": ["كيف أفعّل التحليل الذكي؟", "كيف أختار نطاق تاريخ مخصص؟"],
  "riders-distances": ["كيف أرفع ملف المسافات؟", "ما معنى نسبة الكيلومترات المدفوعة؟"],
  tasks: ["كيف أنشئ مهمة وأسندها لموظف؟", "أين يرى الموظف مهامه؟"],
  employees: ["كيف أضيف موظفاً ومستنداته؟", "كيف أتابع تواريخ انتهاء المستندات؟"],
  salaries: ["كيف أعدّ كشف الرواتب؟", "كيف أخصم سلفة من الراتب؟"],
  advances: ["كيف أسجّل سلفة لموظف؟", "كيف أتابع المديونية؟"],
  "reports-hub": ["ما التقارير المتاحة هنا؟", "كيف أصدّر تقريراً؟"],
  portal: ["كيف أرى عهدتي؟", "أين أجد مهامي؟", "كيف أرى مستند تسليم المركبة؟"],
  finance: ["كيف أنشئ قيد يومية؟", "كيف أصدر فاتورة مبيعات؟", "كيف أحسب ضريبة القيمة المضافة؟"],
};

function getContext() {
  const p = window.location.pathname || "/";
  if (p.startsWith("/employee")) return { label: "بوابة الموظف", key: "portal" };
  if (p.startsWith("/finance")) return { label: "برنامج WorkZone المحاسبي", key: "finance" };
  if (p.startsWith("/corr")) return { label: "بوابة المراسلات", key: "_default" };
  if (p.startsWith("/warehouse")) return { label: "بوابة المستودع", key: "_default" };
  const v = typeof window !== "undefined" ? window.__wzView : null;
  if (v && SECTION_LABELS[v]) return { label: "لوحة التحكم - " + SECTION_LABELS[v], key: v };
  return { label: "لوحة التحكم", key: "_default" };
}

function formatText(text) {
  const esc = (text || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return esc
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\n/g, "<br/>");
}

const GREETING =
  "مرحباً، أنا **براء** مساعدك الذكي في WorkZone. اسألني عن أي خطوة لتشغيل النظام وسأرشدك خطوة بخطوة.";

export const AIAssistant = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [showHint, setShowHint] = useState(false);
  const [hover, setHover] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: GREETING }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [ctx, setCtx] = useState(getContext());
  const sidRef = useRef(localStorage.getItem("wz_baraa_sid") || "");
  const scrollRef = useRef(null);
  const loadedRef = useRef(false);
  const [speakingIdx, setSpeakingIdx] = useState(null);

  const [voiceOn, setVoiceOn] = useState(localStorage.getItem("wz_baraa_voice") !== "0");
  const greetedRef = useRef(false);

  const [pos, setPos] = useState(() => { try { return JSON.parse(localStorage.getItem("wz_baraa_pos")) || { side: "right", bottom: 20 }; } catch { return { side: "right", bottom: 20 }; } });
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);
  const suppressClick = useRef(false);
  const [dlgOpen, setDlgOpen] = useState(false);
  const small = typeof window !== "undefined" && window.innerWidth < 640;

  const onDown = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: r.left, oy: r.top, w: r.width, h: r.height, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) < 6) return;
    d.moved = true;
    setDrag({ x: Math.min(Math.max(4, d.ox + dx), window.innerWidth - d.w - 4), y: Math.min(Math.max(4, d.oy + dy), window.innerHeight - d.h - 4) });
  };
  const onUp = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d?.moved || !drag) return;
    suppressClick.current = true;
    const next = { side: drag.x + d.w / 2 < window.innerWidth / 2 ? "left" : "right", bottom: Math.round(Math.min(Math.max(8, window.innerHeight - drag.y - d.h), window.innerHeight - d.h - 8)) };
    setPos(next); setDrag(null);
    localStorage.setItem("wz_baraa_pos", JSON.stringify(next));
  };

  // hide the launcher while any dialog/sheet is open so it never covers fields
  useEffect(() => {
    const iv = setInterval(() => setDlgOpen(Boolean(document.querySelector("[role='dialog'][data-state='open']") || (window.innerWidth < 1024 && document.querySelector("[data-testid='corr-conversation']")))), 600);
    return () => clearInterval(iv);
  }, []);

  const [touring, setTouring] = useState(false);
  const [offer, setOffer] = useState(null);
  const runTour = useCallback(async (forceMain = false) => {
    stopVoice(); setSpeakingIdx(null); setOpen(false); setOffer(null);
    const c = getContext();
    if (forceMain === true || c.key === "_default") { setTimeout(() => startTour({ voice: voiceOn }), 250); return; }
    localStorage.setItem(`wz_tour_seen_${c.key}`, "1");
    setTouring(true);
    try {
      const steps = await sectionTourSteps(c);
      startTour({ steps, voice: voiceOn });
    } catch { toast.error("تعذّر تجهيز جولة هذا القسم"); }
    finally { setTouring(false); }
  }, [voiceOn]);

  // offer a short intro the first time the user opens each section
  useEffect(() => {
    let last = getContext().key;
    const iv = setInterval(() => {
      const c = getContext();
      if (c.key === last) return;
      last = c.key;
      if (c.key !== "_default" && c.key !== "portal" && !localStorage.getItem(`wz_tour_seen_${c.key}`) && !document.getElementById("baraa-tour")) setOffer(c);
      else setOffer(null);
    }, 1500);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (!offer) return undefined;
    const t = setTimeout(() => setOffer(null), 6000);
    return () => clearTimeout(t);
  }, [offer]);

  const speak = useCallback((idx, raw) => {
    if (speakingIdx === idx) { stopVoice(); setSpeakingIdx(null); return; }
    setSpeakingIdx(idx);
    playVoice(raw, () => setSpeakingIdx((cur) => (cur === idx ? null : cur)));
  }, [speakingIdx]);

  useEffect(() => {
    if (!open && window.speechSynthesis) { window.speechSynthesis.cancel(); setSpeakingIdx(null); }
  }, [open]);
  useEffect(() => () => { if (window.speechSynthesis) window.speechSynthesis.cancel(); }, []);

  useEffect(() => {
    const t = setTimeout(() => setShowHint(true), 2500);
    const t2 = setTimeout(() => setShowHint(false), 12000);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  const openPanel = useCallback(async () => {
    setShowHint(false);
    setCtx(getContext());
    setOpen(true);
    if (!greetedRef.current) {
      greetedRef.current = true;
      timeGreeting().then((g) => setMessages((m) => (m[0]?.role === "assistant" ? [{ ...m[0], text: g }, ...m.slice(1)] : m)));
    }
    if (!loadedRef.current && sidRef.current) {
      loadedRef.current = true;
      try {
        const { messages: hist } = await api.baraaHistory(sidRef.current);
        if (hist && hist.length) {
          setMessages((cur) => [cur[0] || { role: "assistant", text: GREETING }, ...hist.map((m) => ({ role: m.role, ...splitPoint(splitOpen(m.text).text) }))]);
        }
      } catch (e) { /* ignore */ }
    }
  }, []);

  const send = useCallback(async (textArg) => {
    const text = (textArg != null ? textArg : input).trim();
    if (!text || loading) return;
    const context = getContext();
    setCtx(context);
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    const sayLocal = (reply) => {
      let idx = 0;
      setMessages((m) => { idx = m.length; return [...m, { role: "assistant", text: reply }]; });
      if (voiceOn) setTimeout(() => { setSpeakingIdx(idx); playVoice(reply, () => setSpeakingIdx((c) => (c === idx ? null : c))); }, 50);
    };
    const local = matchCommand(text);
    if (local) {
      openTarget(local, navigate);
      if (NAV_TARGETS[local].dialog) setTimeout(() => setOpen(false), 1400);
      sayLocal(`تفضل، فتحت لك **${NAV_TARGETS[local].label}**.`);
      return;
    }
    setLoading(true);
    try {
      const res = await api.baraaChat(sidRef.current, text, context.label, collectUi(), navCatalog());
      if (res.session_id && res.session_id !== sidRef.current) {
        sidRef.current = res.session_id;
        localStorage.setItem("wz_baraa_sid", res.session_id);
      }
      const { text: noOpen, open: openKey } = splitOpen(res.reply);
      const { text: reply, point } = splitPoint(noOpen);
      if (openKey) {
        setTimeout(() => openTarget(openKey, navigate), 300);
        if (NAV_TARGETS[openKey].dialog) setTimeout(() => setOpen(false), 1400);
      }
      let idx = 0;
      setMessages((m) => { idx = m.length; return [...m, { role: "assistant", text: reply, point }]; });
      if (point) setTimeout(() => pointTo(point), 400);
      if (voiceOn) setTimeout(() => { setSpeakingIdx(idx); playVoice(reply, () => setSpeakingIdx((c) => (c === idx ? null : c))); }, 50);
    } catch (e) {
      setMessages((m) => [...m, {
        role: "assistant",
        text: e?.response?.data?.detail || "تعذّر الوصول للمساعد الآن، حاول مرة أخرى بعد قليل.",
      }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, voiceOn, navigate]);

  const suggestions = SUGGESTIONS[ctx.key] || SUGGESTIONS._default;
  const showSuggestions = messages.filter((m) => m.role === "user").length === 0;

  return (
    <>
      {/* Floating launcher */}
      {!open && !dlgOpen && (
        <div
          className={`fixed z-[60] flex items-end gap-2 ${pos.side === "left" && !drag ? "flex-row-reverse" : ""} ${drag ? "" : "transition-[left,right,bottom] duration-300"}`}
          dir="rtl"
          data-testid="baraa-launcher-wrap"
          style={drag ? { left: drag.x, top: drag.y } : { [pos.side]: 12, bottom: pos.bottom }}
        >
          <button
            onClick={() => { if (suppressClick.current) { suppressClick.current = false; return; } openPanel(); }}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
            style={{ touchAction: "none", cursor: drag ? "grabbing" : "grab" }}
            title="اسحبني لأي مكان"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            data-testid="baraa-launcher"
            aria-label="افتح مساعد براء"
            className="relative transition-transform duration-300 hover:scale-105 active:scale-95 drop-shadow-[0_10px_14px_rgba(15,23,42,0.35)]"
          >
            <span className="absolute left-1/2 bottom-0 -translate-x-1/2 h-3 w-12 rounded-full bg-slate-900/25 blur-sm" />
            <BaraaBot size={small ? 46 : 64} track={!drag} mood={drag ? "cheer" : hover ? "cheer" : touring ? "think" : (showHint || offer) ? "wave" : "idle"} />
          </button>
          {offer && !open && !drag && (
            <div data-testid="baraa-section-offer" className="mb-2 max-w-[220px] rounded-2xl rounded-bl-sm bg-white px-3 py-2 text-xs text-slate-700 shadow-lg border border-sky-100 animate-in fade-in slide-in-from-bottom-2">
              هذا قسم <strong className="text-sky-600">{offer.label.replace("لوحة التحكم - ", "")}</strong> — هل تريد أن أعرّفك به؟
              <div className="mt-2 flex gap-1.5">
                <button type="button" data-testid="baraa-section-offer-yes" onClick={() => runTour()} className="flex-1 inline-flex items-center justify-center gap-1 rounded-full bg-sky-500 px-2 py-1 text-[11px] font-bold text-white hover:bg-sky-600 transition-colors">
                  <Compass className="h-3 w-3" /> عرّفني
                </button>
                <button type="button" data-testid="baraa-section-offer-no" onClick={() => { localStorage.setItem(`wz_tour_seen_${offer.key}`, "1"); setOffer(null); }} className="rounded-full bg-slate-100 px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-200 transition-colors">لاحقاً</button>
              </div>
            </div>
          )}
          {touring && !drag && (
            <div data-testid="baraa-tour-loading" className="mb-2 rounded-2xl bg-white px-3 py-2 text-xs text-slate-600 shadow-lg border border-slate-100 inline-flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-sky-500" /> أجهّز لك جولة هذا القسم…
            </div>
          )}
          {showHint && !offer && !drag && (
            <div
              className="mb-2 max-w-[200px] rounded-2xl rounded-bl-sm bg-white px-3 py-2 text-xs text-slate-700 shadow-lg border border-slate-100 animate-in fade-in slide-in-from-bottom-2"
              data-testid="baraa-hint"
            >
              مرحباً، أنا <strong className="text-amber-600">براء</strong> — اضغطني لأساعدك في تشغيل النظام
              {!localStorage.getItem(TOUR_KEY) && (
                <button type="button" data-testid="baraa-hint-tour" onClick={(e) => { e.stopPropagation(); setShowHint(false); runTour(true); }}
                  className="mt-2 flex w-full items-center justify-center gap-1 rounded-full bg-sky-500 px-3 py-1.5 text-[11px] font-bold text-white transition-colors hover:bg-sky-600">
                  <Compass className="h-3.5 w-3.5" /> ابدأ الجولة التعريفية
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Chat panel */}
      {open && (
        <div
          dir="rtl"
          data-testid="baraa-panel"
          className={`fixed bottom-5 ${pos.side === "left" ? "left-5" : "right-5"} z-[60] flex flex-col w-[calc(100vw-2.5rem)] sm:w-[370px] h-[70vh] sm:h-[540px] max-h-[560px] rounded-3xl bg-white shadow-2xl shadow-slate-900/30 border border-slate-200 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300`}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-l from-slate-900 to-slate-800 text-white">
            <div className="h-11 w-11 rounded-full overflow-hidden ring-2 ring-amber-400/60 shrink-0 bg-sky-100 grid place-items-center" data-testid="baraa-header-bot">
              <BaraaBot size={40} headOnly mood={loading ? "think" : speakingIdx !== null ? "talk" : "idle"} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-base leading-tight flex items-center gap-1">
                براء <Sparkles className="h-3.5 w-3.5 text-amber-400" />
              </div>
              <div className="text-[11px] text-slate-300 truncate">مساعد WorkZone — {ctx.label}</div>
            </div>
            <button onClick={runTour} data-testid="baraa-tour-header" title="جولة تعريفية"
              className="h-8 w-8 grid place-items-center rounded-full hover:bg-white/10 transition-colors">
              <Compass className="h-4 w-4 text-sky-300" />
            </button>
            <button
              onClick={() => { const v = !voiceOn; setVoiceOn(v); localStorage.setItem("wz_baraa_voice", v ? "1" : "0"); if (!v) { stopVoice(); setSpeakingIdx(null); } }}
              data-testid="baraa-voice-toggle"
              title={voiceOn ? "إيقاف القراءة التلقائية" : "تشغيل القراءة التلقائية"}
              className="h-8 w-8 grid place-items-center rounded-full hover:bg-white/10 transition-colors"
            >
              {voiceOn ? <Volume2 className="h-4 w-4 text-amber-300" /> : <VolumeX className="h-4 w-4 text-slate-400" />}
            </button>
            <button
              onClick={() => { stopVoice(); setSpeakingIdx(null); setOpen(false); }}
              data-testid="baraa-close"
              aria-label="إغلاق"
              className="h-8 w-8 grid place-items-center rounded-full hover:bg-white/10 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-slate-50" data-testid="baraa-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-start" : "justify-end"}`}>
                {m.role === "assistant" && (
                  <div className="h-7 w-7 rounded-full overflow-hidden ring-1 ring-slate-200 ml-2 shrink-0 self-end bg-sky-100 grid place-items-center">
                    <BaraaBot size={26} headOnly track={false} mood={speakingIdx === i ? "talk" : "idle"} />
                  </div>
                )}
                {m.role === "user" ? (
                  <div
                    className="max-w-[80%] rounded-2xl rounded-br-sm px-3.5 py-2.5 text-[13px] leading-relaxed shadow-sm bg-slate-800 text-white"
                    dangerouslySetInnerHTML={{ __html: formatText(m.text) }}
                  />
                ) : (
                  <div className="max-w-[80%] rounded-2xl rounded-bl-sm px-3.5 py-2.5 shadow-sm bg-white text-slate-700 border border-slate-100">
                    <div className="text-[13px] leading-relaxed" dangerouslySetInnerHTML={{ __html: formatText(m.text) }} />
                    {m.point && (
                      <button onClick={() => pointTo(m.point)} data-testid={`baraa-show-${i}`}
                        className="mt-1.5 ml-3 inline-flex items-center gap-1 text-[11px] font-semibold text-sky-600 hover:text-sky-700 transition-colors">
                        <MousePointerClick className="h-3.5 w-3.5" /> أرني الزر
                      </button>
                    )}
                    <button
                      onClick={() => speak(i, m.text)}
                      data-testid={`baraa-speak-${i}`}
                      className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-amber-600 transition-colors"
                    >
                      {speakingIdx === i ? <Square className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
                      {speakingIdx === i ? "إيقاف" : "استماع"}
                    </button>
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex justify-end" data-testid="baraa-typing">
                <div className="h-7 w-7 rounded-full overflow-hidden ring-1 ring-slate-200 ml-2 shrink-0 bg-sky-100 grid place-items-center">
                  <BaraaBot size={26} headOnly track={false} mood="think" />
                </div>
                <div className="bg-white border border-slate-100 rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-2 w-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="h-2 w-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}

            {showSuggestions && !loading && (
              <div className="flex flex-wrap gap-2 justify-end pt-1" data-testid="baraa-suggestions">
                <button onClick={runTour} data-testid="baraa-start-tour"
                  className="text-[12px] bg-sky-500 text-white rounded-full px-3 py-1.5 hover:bg-sky-600 transition-colors shadow-sm inline-flex items-center gap-1">
                  <Compass className="h-3.5 w-3.5" /> {getContext().key === "_default" ? "جولة تعريفية في النظام" : "عرّفني بهذا القسم"}
                </button>
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s)}
                    data-testid={`baraa-suggestion-${i}`}
                    className="text-[12px] bg-white border border-amber-200 text-amber-700 rounded-full px-3 py-1.5 hover:bg-amber-50 transition-colors shadow-sm"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-100 p-2.5 bg-white">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                data-testid="baraa-input"
                rows={1}
                placeholder="اكتب سؤالك هنا…"
                className="flex-1 resize-none max-h-24 rounded-2xl border border-slate-200 px-3.5 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-amber-400/50 focus:border-amber-300"
              />
              <button
                onClick={() => send()}
                disabled={loading || !input.trim()}
                data-testid="baraa-send"
                aria-label="إرسال"
                className="h-10 w-10 grid place-items-center rounded-full bg-amber-500 text-white shrink-0 transition-all hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5 -scale-x-100" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIAssistant;
